import { useEffect, useState } from 'react';
import { Building2, CalendarClock, Handshake, Phone, Sparkles } from '../components/icons';
import Card from '../components/Card';
import { crmService } from '../services/crmService';
import type { DashboardData } from '../types';

const LOADING_STEPS = [
  'Autenticando rotas e contexto comercial...',
  'Coletando métricas de empresas e agenda...',
  'Consolidando cards executivos e pipeline...',
  'Finalizando renderização do dashboard...'
];
const formatDatePtBr = (value: string) => {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);

  useEffect(() => {
    crmService
      .getDashboard()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading) {
      setLoadingStep(0);
      return;
    }
    const timer = window.setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % LOADING_STEPS.length);
    }, 1300);
    return () => window.clearInterval(timer);
  }, [loading]);

  const metrics = [
    {
      title: 'Total empresas captadas',
      value: `${data?.totalEmpresas ?? 0}`,
      icon: Building2,
      iconClassName: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300'
    },
    {
      title: 'Agente ativo',
      value: data?.agenteAtivo ? 'Ativo' : 'Inativo',
      icon: Handshake,
      iconClassName: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Resumo executivo</p>
        <h1 className="mt-1 text-3xl font-black">Dashboard Comercial</h1>
      </div>

      {loading ? (
        <Card className="dashboard-loading-card overflow-hidden p-0">
          <div className="google-search-grid" />
          <div className="google-search-scanline" />
          <div className="google-search-progress" />
          <div className="dashboard-loading-layout">
            <div className="dashboard-loading-panel">
              <p className="dashboard-loading-kicker">Realtime Pipeline</p>
              <p className="dashboard-loading-headline">Coletando dados comerciais</p>
              <p className="dashboard-loading-copy">{LOADING_STEPS[loadingStep]}</p>

              <div className="dashboard-loading-bars">
                {Array.from({ length: 5 }).map((_, index) => (
                  <span key={`bar-${index}`} className="dashboard-loading-bar" style={{ animationDelay: `${index * 0.12}s` }} />
                ))}
              </div>

              <div className="grid gap-2">
                {LOADING_STEPS.map((step, index) => (
                  <div
                    key={step}
                    className={`google-search-step rounded-xl border px-3 py-2 text-xs transition-all duration-300 ${
                      index === loadingStep
                        ? 'google-search-step-active border-primary/50 bg-primary/10 text-slate-900 dark:border-cta/50 dark:bg-cta/15 dark:text-slate-100'
                        : 'border-borderLight/70 bg-white/40 text-slate-500 dark:border-borderDark/70 dark:bg-slate-900/20 dark:text-slate-400'
                    }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-loading-scene-wrap">
              <div className="dashboard-loading-glow" />
              <div className="google-search-scene">
                <div className="google-search-beam" />
                <div className="google-search-ring google-search-ring-a" />
                <div className="google-search-ring google-search-ring-b" />
                <div className="google-search-orbit google-search-orbit-inner" />
                <div className="google-search-orbit google-search-orbit-outer" />
                <div className="google-search-core">
                  <Sparkles className="h-5 w-5" />
                </div>
              </div>
              <p className="google-search-title text-sm font-bold text-slate-800 dark:text-slate-100">Dashboard Intelligence</p>
            </div>
          </div>
        </Card>
      ) : null}

      {!loading ? (
        <>
          <section className="grid gap-4 sm:grid-cols-2">
            {metrics.map((metric) => {
              const Icon = metric.icon;
              return (
                <Card key={metric.title} className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 ring-black/5 dark:ring-white/10 ${metric.iconClassName}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                  </div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">{metric.title}</p>
                  <p className="mt-1 text-2xl font-black">{metric.value}</p>
                </Card>
              );
            })}
          </section>

          <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <h2 className="mb-3 text-lg font-semibold">Últimas empresas</h2>
              <Card className="space-y-3">
                {!data?.ultimasEmpresas.length ? (
                  <p className="text-sm text-slate-500">Sem empresas recentes.</p>
                ) : (
                  data.ultimasEmpresas.map((empresa) => (
                    <div
                      key={empresa.id}
                      className="rounded-xl border border-borderLight bg-white/70 p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-borderDark dark:bg-slate-900/35"
                    >
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="inline-flex items-center gap-2 font-semibold">
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-cta/20 dark:text-cta">
                            <Building2 className="h-4 w-4" />
                          </span>
                          {empresa.nome_empresa}
                        </p>
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:border-amber-300/25 dark:bg-amber-500/10 dark:text-amber-300">
                          <Sparkles className="h-3.5 w-3.5" /> {empresa.rating} ({empresa.reviews})
                        </span>
                      </div>

                      <div className="grid gap-2 text-xs text-slate-600 dark:text-slate-300 sm:grid-cols-2">
                        <p className="inline-flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5 text-slate-400" />
                          {empresa.telefone || '-'}
                        </p>
                        <p className="inline-flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-slate-400" />
                          {empresa.endereco || '-'}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </Card>
            </div>

            <div>
              <h2 className="mb-3 text-lg font-semibold">Próximos compromissos</h2>
              <Card className="space-y-3">
                {!data?.proximosCompromissos.length ? (
                  <p className="text-sm text-slate-500">Sem compromissos próximos.</p>
                ) : (
                  data.proximosCompromissos.map((item) => (
                    <div key={item.id} className="rounded-xl border border-borderLight p-3 dark:border-borderDark">
                      <p className="font-semibold">{item.titulo}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-300">
                        {formatDatePtBr(item.data)} · {item.hora}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">{item.descricao}</p>
                    </div>
                  ))
                )}
              </Card>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
